import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import { Typography } from '@mui/material';
import { UseModalDispatch } from '@lwtears/lwt-common-frontend/lib/Modal';
import withModal from '@lwtears/lwt-common-frontend/lib/Modal/withModal';
import { createEvent } from 'api';
import {
  AppStoreState,
  LiteResource,
  FilterGroup,
  WorkbookState,
  Resource,
  UserFavorite,
  Frametype
} from 'types';
import {
  setResourceFilters,
  toggleTag,
  selectResource,
  updateAppCounter,
  toggleAppLoadingScreenVisibility,
  clearResourceFilters
} from 'state/modules/app';
import { resetResources } from 'state/modules/entity/custom/resource-favorites-mutations/actions';
import {
  fetchResources,
  fetchFilterTags,
  createUserFavorite,
  removeUserFavorite,
  fetchFavoriteResources
} from 'state/modules/entity/custom/resource-favorites-mutations/thunks/resource-thunks';
import { fetchClickAways } from 'state/modules/user/thunks';
import { getFilteredResources } from 'state/modules/entity/selectors/resource-selectors';
import { getFilteredClickAways } from 'state/modules/user/selectors/click-away-selectors';
import withStyles from 'components/@layout/withStyles';
import { getCurrentWorkbook } from 'state/modules/entity/selectors/workbook-selectors';
import { UserState } from 'state/modules/user';
import { translate } from 'translations/translator';
import ResourcesTranslationVariant from 'translations/variants/resources';
import withRouter from '../../../components/App/hooks/with-router';
import ResourceModal from '../../../components/@common/Modals/ResourcePlayer';
import ResourcesList from '../../../components/Resources/ResourcesList/ResourcesList';
import ResourceFilterBreadcrumbs from '../../../components/Resources/ResourceFilterBreadcrumbs';
import { ResourcesSections, getSwitchFrameType } from '../../../util/resources-util';

type Props = {
  clickAways: Array<Resource>;
  filterGroups: Array<FilterGroup>;
  locale: string;
  resources: Array<Resource>;
  selectedFilterTags: Array<number>;
  searchText: string;
  fetchResources: (bookId?: number) => Array<Resource>;
  fetchClickAways: (bookId?: number) => Array<Resource>;
  fetchFavoriteResources: (productCurriculumId?: number) => Array<UserFavorite>;
  fetchFilterTags: (bookId?: number) => Array<FilterGroup>;
  selectedResource: LiteResource;
  showResourcePlayer: boolean;
  counter: number;
  workbooksById: WorkbookState;
  selectResource: (resource: LiteResource) => void;
  createUserFavorite: (resource: Resource) => void;
  removeUserFavorite: (favoriteId: number) => void;
  toggleAppLoadingScreenVisibility: () => void;
  setFilters: (
    searchText?: string | null,
    tagIds?: Array<number>,
    resourcesSection?: string
  ) => void;
  clearResourceFilters: () => void;
  toggleTag: (tagId: number) => void;
  resetResources: () => void;
  updateCounter: (operationType: string) => number;
  src: string;
  show?: boolean;
  title: string;
  resourceType: any | '';
  mediaType: string;
  aspectRatio?: string;
  heightAdjust?: number;
  fileExtension: string;
  loadedData: () => void;
  onClose: () => void;
  frameType: Frametype;
  classes: any;
  currentWorkbook: any;
  user: UserState | any;
  handleOnClose: () => void;
  router: any;
} & UseModalDispatch;

const t = (key: ResourcesTranslationVariant, options?) => translate('resources', key, options);

class AllResources extends Component<Props> {
  componentDidMount() {
    this.fetchData();
    this.props.clearResourceFilters();
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.locale !== prevProps.locale ||
      this.props.currentWorkbook?.id !== prevProps.currentWorkbook?.id
    ) {
      this.fetchData();
    }
  }
  getFilterTags = currentWorkbook => {
    if (currentWorkbook?.id) {
      this.props.fetchFilterTags(currentWorkbook?.id);
    }
  };
  getClickAways = currentWorkbook => {
    if (currentWorkbook?.id) {
      this.props.fetchClickAways(currentWorkbook?.id);
    }
  };
  getFavoriteResources = currentWorkbook => {
    if (currentWorkbook?.products?.length) {
      this.props.fetchFavoriteResources(currentWorkbook?.products[0]?.productCurriculumId);
    }
  };
  fetchData() {
    const { currentWorkbook } = this.props;
    this.props.resetResources();
    this.props.updateCounter('reset');

    if (currentWorkbook?.id) {
      this.getFilterTags(currentWorkbook);
      if (Object.entries(this.props.workbooksById).length > 0) {
        this.props.fetchResources(currentWorkbook?.id);
      }
      this.getClickAways(currentWorkbook);
      this.getFavoriteResources(currentWorkbook);
    }
  }

  handleSelectResource = async (resource: LiteResource) => {
    await this.props.selectResource(resource);
    this.openResourceModal(resource);
  };

  handlePinResource = (resource: Resource): void => {
    if (!resource.favorites || resource.favorites.length === 0)
      this.props.createUserFavorite(resource);
  };
  handleRemoveFavorite = (resource: Resource) => {
    if (resource.favorites && resource.favorites.length > 0) {
      this.props.removeUserFavorite(resource.favorites[0].id);
    }
  };

  createTelemetryEvent = () => {
    const { selectedResource } = this.props;

    createEvent('accessResource', {
      section: 'resources',
      resourceId: selectedResource.id,
      resourceType: selectedResource.type
    });
  };

  openResourceModal = (selectedResource): void => {
    this.props.openDraggableModal({
      titleText: (
        <span
          style={{
            display: 'flex',
            height: '100%',
            alignItems: 'center'
          }}
        >
          {selectedResource.title}
        </span>
      ) as any,
      body: (
        <ResourceModal
          selectedResource={selectedResource}
          frameType={this.props.frameType}
          handleClose={this.props.handleOnClose}
          eventLogger={this.createTelemetryEvent}
        />
      ),
      options: { closeOnBackdropClick: true },
      getContainerStyles: defaultStyles => ({
        ...defaultStyles,
        backgroundColor: '#fff'
      }),
      getPaperStyles: defaultStyles => ({
        ...defaultStyles,
        ...(selectedResource?.mediaType === 'video' && { maxHeight: '85vh' })
      })
    });
  };

  searchText = debounce((searchText: string) => {
    const oldValue = this.props.searchText;

    if (searchText.length > 2 || oldValue !== '') {
      this.props.setFilters(searchText, []);
    }
  }, 300);

  onToggleTag = tagId => {
    this.props.toggleTag(tagId);
  };

  getResources = () => {
    return this.props.selectedFilterTags.length && this.props.resources.length
      ? this.props.resources
      : this.props.resources.concat(this.props.clickAways);
  };
  render(): React.ReactElement {
    const {
      classes: { allResources }
    } = this.props;

    return (
      <div className={allResources}>
        <br />
        <div style={{ display: 'flex', flex: 1 }}>
          <Typography variant="h4" style={{ marginLeft: '30px', marginRight: '15px' }}>
            {t(ResourcesTranslationVariant.filteredBy)}
          </Typography>
          <ResourceFilterBreadcrumbs />
        </div>
        <ResourcesList
          resourcesItems={this.getResources()}
          onSelectItem={this.handleSelectResource}
          onClickPinIcon={this.handlePinResource}
          onRemoveItem={this.handleRemoveFavorite}
        />
      </div>
    );
  }
}

const styles: any = {
  allResources: {
    height: '100%',
    width: '100%',
    overflow: 'auto'
  }
};

const mapStateToProps = (state: AppStoreState, { router: { params } }: Props) => {
  return {
    clickAways: getFilteredClickAways(state, ResourcesSections.ALLRESOURCES.code),
    counter: state.app.counter,
    filterGroups: state.entity.filterGroups,
    locale: state.app.locale,
    resources: getFilteredResources(
      { ...cloneDeep(state.entity), ...cloneDeep(state.app) },
      ResourcesSections.ALLRESOURCES.code
    ),
    searchText: state.app.resource.selectedFilters.searchText,
    selectedFilterTags: state.app.resource.selectedFilters.selectedTags,
    selectedResource: state.app.resource.selectedResource,
    showResourcePlayer: state.app.resource.showResourcePlayer,
    workbooksById: state.entity.workbooksById,
    frameType: getSwitchFrameType(
      state.app.resource.selectedResource.type,
      state.app.resource.selectedResource.mediaType
    ),
    currentWorkbook: getCurrentWorkbook(state, params),
    user: state.user
  };
};

export default compose(
  withRouter,
  withModal,
  withStyles(styles),
  connect(mapStateToProps, {
    setFilters: setResourceFilters,
    clearResourceFilters,
    toggleTag,
    resetResources,
    updateCounter: updateAppCounter,
    fetchResources,
    fetchClickAways,
    fetchFilterTags,
    selectResource,
    createUserFavorite,
    removeUserFavorite,
    fetchFavoriteResources,
    toggleAppLoadingScreenVisibility
  })
)(AllResources as any);
